import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { dashboardService } from '@/services/dashboard';
import { leagueService } from '@/services/league';
import { useAuth } from '@/contexts/AuthContext';
import { useMyStanding } from '@/hooks/useMyStanding';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function Summary() {
  const { user } = useAuth();
  const { standing, isEliminated, eliminatedInGameweek } = useMyStanding();
  const [countdown, setCountdown] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', user?.id],
    queryFn: () => dashboardService.getDashboard(user?.id || ''),
    enabled: !!user?.id,
  });

  // Fetch league standings to check elimination status
  const { data: leagueData } = useQuery({
    queryKey: ['league-standings'],
    queryFn: () => leagueService.getStandings(),
  });

  // Calculate countdown to deadline - must be before early return
  useEffect(() => {
    if (!data || isLoading) return;

    const upcomingGameweeks = data.upcomingGameweeks;
    const nextGameweek = upcomingGameweeks.length > 0 ? upcomingGameweeks[0] : null;
    const isInProgress = nextGameweek?.status === 'InProgress';

    if (!nextGameweek || isInProgress) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCountdown('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const deadline = new Date(nextGameweek.deadline);
      const diff = deadline.getTime() - now.getTime();

      if (diff <= 0) {
        setCountdown('Deadline passed');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setCountdown(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m`);
      } else {
        setCountdown(`${minutes}m`);
      }
    };

    // Update immediately
    updateCountdown();

    // Update every minute
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [data, isLoading]);

  if (isLoading || !data) {
    return (
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="py-2">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-xs font-medium">Loading...</CardTitle>
            </CardHeader>
            <CardContent className="pb-3">
              <div className="text-lg font-bold">--</div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const { user: userStats, upcomingGameweeks } = data;

  // Calculate active players count
  const activePlayers = leagueData?.standings.filter((s) => !s.isEliminated).length || 0;
  const userPosition = standing?.position;

  // Helper function to add ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinal = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const nextGameweek = upcomingGameweeks.length > 0 ? upcomingGameweeks[0] : null;
  const isInProgress = nextGameweek?.status === 'InProgress';
  const formattedDeadline = nextGameweek
    ? new Date(nextGameweek.deadline).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'TBD';

  return (
    <div className="space-y-3">
      {/* Elimination Warning */}
      {isEliminated && (
        <Alert className="bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800">
          <AlertDescription className="text-red-800 dark:text-red-200">
            <strong>⚠️ You have been eliminated from the competition.</strong>
            {eliminatedInGameweek && (
              <p className="mt-1">
                You were eliminated after Gameweek {eliminatedInGameweek}. You cannot make new
                picks, and the league table now shows only players still in. Your season is kept on
                your{' '}
                {/* The player page, not /profile — that one is account settings (name, photo,
                    password) and has nothing of their season on it. This is where the record
                    they are being pointed at actually lives. */}
                <Link
                  to={user ? `/users/${user.id}` : '/users'}
                  className="underline underline-offset-4"
                  data-testid="eliminated-player-page-link"
                >
                  player page
                </Link>{' '}
                and on the{' '}
                <Link to="/eliminations" className="underline underline-offset-4">
                  eliminations page
                </Link>
                .
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <Card className="py-2">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-xs font-medium">Current Gameweek</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-lg font-bold">
              {nextGameweek ? `GW ${nextGameweek.weekNumber}` : 'No GW'}
            </div>
            <p className="text-xs text-muted-foreground">
              {isInProgress ? (
                <span className="text-amber-600 dark:text-amber-400 font-medium">In Progress</span>
              ) : countdown ? (
                <span className="text-blue-600 dark:text-violet-300 font-medium">
                  {countdown} until deadline
                </span>
              ) : (
                `Deadline: ${formattedDeadline}`
              )}
            </p>
          </CardContent>
        </Card>

        <Card className="py-2">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-xs font-medium">League Position</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-lg font-bold">
              {userPosition ? getOrdinal(userPosition) : '--'}
            </div>
            <p className="text-xs text-muted-foreground">
              {activePlayers > 0 ? `of ${activePlayers}` : 'No standings'}
            </p>
          </CardContent>
        </Card>

        <Card className="py-2">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-xs font-medium">Your Points</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-lg font-bold">{userStats.totalPoints}</div>
            <p className="text-xs text-muted-foreground">{userStats.totalPicks} picks made</p>
          </CardContent>
        </Card>

        <Card className="py-2">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-xs font-medium">Your Record</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-lg font-bold">
              {userStats.totalWins}-{userStats.totalDraws}-{userStats.totalLosses}
            </div>
            <p className="text-xs text-muted-foreground">W-D-L</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
